import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicCustomFormsAPI } from '../services/api';
import { useToast } from '../context/ToastContext';

/**
 * DynamicTerms - Project-specific Terms and Conditions page
 * Route: /terminos/:slug
 * 
 * Falls back to default Terms content if no project-specific content exists
 */
const DynamicTerms = () => {
  const { slug } = useParams();
  const toast = useToast();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProjectSpecific, setIsProjectSpecific] = useState(false);
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    loadTermsContent();
  }, [slug]);

  const loadTermsContent = async () => {
    if (!slug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await publicCustomFormsAPI.getDocument(slug, 'terms');
      if (response.data && response.data.content) {
        setContent(response.data.content);
        setIsProjectSpecific(true);
        if (response.data.projectName) {
          setProjectName(response.data.projectName);
        }
      } else {
        setIsProjectSpecific(false);
      }
    } catch (error) {
      console.log('No custom terms found for this project, using default');
      setIsProjectSpecific(false);
    } finally {
      setLoading(false);
    }
  };

  // Default terms content (same as Terms.js)
  const defaultTerms = (
    <div className="prose max-w-none text-gray-700 space-y-6">
      <p className="text-sm text-gray-500">
        Última actualización: Marzo 2026
      </p>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Aceptación de Términos</h2>
        <p>
          Al acceder y utilizar este sistema de registro de eventos, usted acepta estar bound by 
          estos Términos y Condiciones. Si no está de acuerdo con alguno de estos términos, 
          por favor no utilice nuestro servicio.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Descripción del Servicio</h2>
        <p>
          Nuestro sistema proporciona una plataforma para el registro de asistentes a eventos, 
          generación de códigos QR de acceso y, opcionalmente, reconocimiento facial para 
          check-in automatizado.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Obligaciones del Usuario</h2>
        <p>Al utilizar este servicio, usted se compromete a:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Proporcionar información veraz y actualizada</li>
          <li>Mantener la confidencialidad de sus credenciales de acceso</li>
          <li>No utilizar el servicio para fines ilícitos</li>
          <li>No intentar vulnerar la seguridad del sistema</li>
          <li>Respetar los derechos de otros usuarios</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Registro y Cuenta</h2>
        <p>
          Para acceder a ciertas funcionalidades, es posible que deba crear una cuenta. 
          Usted es responsable de mantener la confidencialidad de su cuenta y contraseña, 
          y de todas las actividades que ocurran bajo su cuenta.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Uso de Datos Biométricos</h2>
        <p>
          El reconocimiento facial es <strong>totalmente opcional</strong>. Si usted decide 
          utilizar esta función, su descriptor facial será:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Almacenado de forma encriptada</li>
          <li>Utilizado únicamente para propósitos de identificación en el evento</li>
          <li>Eliminado después de finalizada la duración del evento</li>
          <li>No compartido con terceros</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Propiedad Intelectual</h2>
        <p>
          El contenido, diseño y código fuente de este sistema son propiedad del 
          titular del servicio. Queda prohibida la reproducción, distribución o 
          modificación sin autorización previa.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Limitación de Responsabilidad</h2>
        <p>
          El servicio se proporciona "tal cual". No garantizamos que el servicio 
          sea ininterrumpido, seguro o libre de errores. En ningún caso seremos 
          responsables por daños indirectos, incidentales o consecuenciales.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Modificaciones</h2>
        <p>
          Nos reservamos el derecho de modificar estos términos en cualquier momento. 
          Las modificaciones entrarán en vigor desde su publicación en el sitio.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Ley Aplicable</h2>
        <p>
          Estos términos se rigen por las leyes de la República del Perú. 
          Cualquier disputa será resuelta en los tribunales de Lima, Perú.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Contacto</h2>
        <p>
          Para consultas sobre estos términos, contacte al administrador del evento 
          o a través de los canales proporcionados en el formulario de registro.
        </p>
      </section>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 flex items-center justify-center">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          {isProjectSpecific && projectName 
            ? `Términos y Condiciones - ${projectName}`
            : 'Términos y Condiciones del Servicio'
          }
        </h1>
        
        {isProjectSpecific && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Estos son los términos y condiciones específicos para el evento al que te estás registrando.
              También aplican los términos generales del servicio.
            </p>
          </div>
        )}

        {isProjectSpecific && content ? (
          <div 
            className="prose max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          defaultTerms
        )}

        <div className="mt-8 pt-6 border-t border-gray-200">
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DynamicTerms;
